// CC-53 — /experiments listing page. Minimal, functional.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api/experiments";

export default function Experiments() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState("any");
  const [status, setStatus]   = useState("experimental");
  const [sel, setSel]         = useState({ a: "", b: "" });

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (product !== "any") qs.set("product", product);
      qs.set("status", status);
      qs.set("limit", "100");
      const r = await fetch(`${API}/list?${qs.toString()}`, { credentials: "include" });
      const j = await r.json();
      setRows(j.experiments || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [product, status]);

  const toggleSelect = (id) => {
    setSel((cur) => {
      if (cur.a === id) return { a: "", b: cur.b };
      if (cur.b === id) return { a: cur.a, b: "" };
      if (!cur.a) return { a: id, b: cur.b };
      if (!cur.b) return { a: cur.a, b: id };
      return { a: id, b: cur.b };
    });
  };

  const compareHref = useMemo(() => {
    if (sel.a && sel.b)
      return `/experiments/compare?a=${sel.a}&b=${sel.b}`;
    return "";
  }, [sel]);

  return (
    <div className="p-6 space-y-5" data-testid="experiments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Experiments</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Experimental briefs created by Claude Pro via MCP.
            TTL: 60 days. Promote to Production after review.
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-experiment-btn">
                + Add Experiment
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Experiments are created via Claude Pro</DialogTitle>
                <DialogDescription className="pt-2 leading-relaxed">
                  Experiments are created through the MCP tool&nbsp;
                  <code className="bg-muted px-1 py-0.5 rounded text-xs">
                    create_experimental_brief
                  </code>
                  . Open <a className="underline" href="https://claude.ai" target="_blank" rel="noreferrer">claude.ai</a>
                  &nbsp;and ask Claude to generate an alternative brief for
                  comparison with the production cron.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter />
            </DialogContent>
          </Dialog>
          {sel.a && sel.b ? (
            <Link to={compareHref}>
              <Button data-testid="compare-selected-btn">
                Compare {sel.a.slice(0, 6)} ↔ {sel.b.slice(0, 6)}
              </Button>
            </Link>
          ) : (
            <Button disabled>Select 2 to compare</Button>
          )}
        </div>
      </div>

      <div className="flex gap-3 items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Product:</span>
          <Select value={product} onValueChange={setProduct}>
            <SelectTrigger className="w-40" data-testid="filter-product">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quotes">Quotes</SelectItem>
              <SelectItem value="survey">Survey</SelectItem>
              <SelectItem value="numbers">Numbers</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Status:</span>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="experimental">Experimental</SelectItem>
              <SelectItem value="generated">Generated</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="any">Any</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {loading ? "Loading…" : `${rows.length} briefs`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="experiments-table">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-3 text-left">Pick</th>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Product</th>
                  <th className="p-3 text-left">Label</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Created</th>
                  <th className="p-3 text-left">Expires</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const picked = sel.a === r.id || sel.b === r.id;
                  return (
                    <tr key={r.id} className={picked ? "bg-primary/10" : "border-t"}>
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={picked}
                          onChange={() => toggleSelect(r.id)}
                          data-testid={`pick-${r.id}`}
                        />
                      </td>
                      <td className="p-3 font-mono text-xs">{r.date}</td>
                      <td className="p-3">{r.product}</td>
                      <td className="p-3 font-mono text-xs">
                        {r.experiment_label || <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="p-3">
                        <Badge variant={r.status === "experimental" ? "secondary" : "outline"}>
                          {r.status}
                        </Badge>
                      </td>
                      <td className="p-3 font-mono text-xs">
                        {r.created_at?.slice(0, 19).replace("T", " ")}
                      </td>
                      <td className="p-3 font-mono text-xs">
                        {r.expires_at
                          ? r.expires_at.slice(0, 10)
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted-foreground">
                      No experiments yet. Ask Claude Pro to generate one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
